import React from "react";
import { EntityEditProvider } from "@/contexts/EntityEditContext";
import type { EntityEditProviderProps } from "@/types/contexts/entityEdit";
import { ContactType } from "@/types/db/contacts";
import { contactService } from "@/services/contactService";
import { FromDB } from "@/types";
import { supabase } from "@/lib/supabase-client";

interface ContactEditProviderProps extends Omit<EntityEditProviderProps<ContactType>, "onSave" | "entityName"> {
  children: React.ReactNode;
}

export function ContactEditProvider({ children, entityData, loading, onSaveSuccess }: ContactEditProviderProps) {
  const handleSave = async (
    formData: Partial<FromDB<ContactType>>,
    metadata?: Record<string, any>,
  ): Promise<ContactType> => {
    if (!entityData?.id) {
      throw new Error("No contact ID available");
    }

    // Check if email changed and validate it doesn't already exist
    const newEmail = formData.email_address?.trim().toLowerCase();
    const originalEmail = entityData.email_address?.trim().toLowerCase();
    if (newEmail && newEmail !== originalEmail) {
      const { data: existingContacts } = await supabase
        .from("contacts")
        .select("id")
        .ilike("email_address", newEmail)
        .neq("id", entityData.id)
        .limit(1);

      if (existingContacts && existingContacts.length > 0) {
        throw new Error("A contact with this email address already exists");
      }
    }

    // Strip out virtual/joined fields and read-only fields
    const {
      id,
      created_at,
      updated_at,
      contact_clients, // Link table data - strip this out
      ...cleanFormData
    } = formData;

    // Handle avatar upload if present in metadata
    if (metadata?.avatarFile) {
      await contactService.uploadAvatar(entityData.id, metadata.avatarFile);
    } else if (metadata?.avatarFile === null) {
      // User explicitly removed avatar
      await contactService.deleteAvatar(entityData.id);
    }

    // Update the base contacts table with only direct columns
    const updatedContact = await contactService.updateContact(entityData.id, cleanFormData);

    // Sync link table separately if it was modified
    if (contact_clients !== undefined) {
      await contactService.syncContactClients(entityData.id, contact_clients || []);
    }

    // Fetch complete data with joins to return
    const completeContact = await contactService.getContactById(entityData.id);
    if (!completeContact) {
      throw new Error("Failed to fetch updated contact data");
    }

    return completeContact;
  };

  return (
    <EntityEditProvider
      entityData={entityData}
      loading={loading}
      onSave={handleSave}
      onSaveSuccess={onSaveSuccess}
      entityName="Contact"
    >
      {children}
    </EntityEditProvider>
  );
}
